import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { Breadcrumbs } from '../../components/Breadcrumbs'
import { apiErrorMessage } from '../../lib/errors'
import { formatNumber } from '../../lib/format'
import type { Load } from '../loads/api'
import { equipmentLabel } from '../loads/equipment'
import { getLoadMatches, type LoadMatches, type PostingMatch } from './api'
import { BrokerContact } from './BrokerContact'
import { availableDays, pickupDays, routeLine } from './describe'
import { cautionSentences, exclusionSentences, factorPhrases } from './reasons'
import { unitEquipment, unitName } from '../availability/unitName'

/**
 * The other door onto the same judgement: the posted units that could take a load,
 * closest first — for one load the rate is the same for every unit, so the deadhead
 * decides — and the open ones that could not, each with every reason.
 */
export function LoadMatchesPage() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()

  const [matches, setMatches] = useState<LoadMatches | null>(null)
  // Stored unresolved and translated at render, so a language switch re-translates it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null)

  useEffect(() => {
    if (id === undefined) {
      return
    }

    let active = true
    setMatches(null)
    setFailure(null)

    getLoadMatches(id)
      .then((found) => {
        if (active) {
          setMatches(found)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setFailure({ error: caught })
        }
      })

    return () => {
      active = false
    }
  }, [id])

  if (failure !== null) {
    return (
      <section className="page">
        <Breadcrumbs
          trail={[
            { label: t('nav.loads'), to: '/loads' },
            { label: null, to: `/loads/${id}` },
            { label: t('matching.openUnits') },
          ]}
        />
        <p className="alert" role="alert">
          {apiErrorMessage(failure.error, t, 'matching.loadError')}
        </p>
      </section>
    )
  }

  if (matches === null) {
    return <p className="session-check">{t('matching.loading')}</p>
  }

  const { load } = matches
  const open = load.standing === 'Open'
  // Matching sets aside every load of a broker we stopped working with or whose paperwork
  // lapsed, so this page never invites a call to one (GPT-5.6 Sol on #35).
  const brokerInPlay = load.brokerActive && load.brokerComplianceState !== 'Blocked'
  const language = i18n.language

  return (
    <section className="page">
      <Breadcrumbs
        trail={[
          { label: t('nav.loads'), to: '/loads' },
          { label: routeLine(load, t), to: `/loads/${load.id}` },
          { label: t('matching.openUnits') },
        ]}
      />
      <header className="page__header page__header--row">
        <div>
          <h1>{t('matching.unitsTitle', { route: routeLine(load, t) })}</h1>
          <p className="page__subtitle">{t('matching.unitsSubtitle')}</p>
        </div>
        <Link className="button button--quiet" to={`/loads/${load.id}`}>
          {t('matching.backToLoad')}
        </Link>
      </header>

      <p className="matches__subject">
        {[
          equipmentLabel(load, t),
          t('loads.book.freightLine', {
            loadType: t(`availability.loadTypes.${load.loadType}`),
            pounds: formatNumber(load.weightPounds, language),
          }),
          pickupDays(load, t, language),
          load.brokerName,
        ].join(' · ')}
      </p>

      {!open && <p className="placeholder">{t('matching.loadNotOpen')}</p>}

      {/* One broker for every unit below, so one way to reach it: the call that follows
          reading this list is to the broker (Copilot on #35). */}
      {open && brokerInPlay && (
        <div className="matches__contact">
          <BrokerContact load={load} brokerPhone={matches.brokerPhone} brokerEmail={matches.brokerEmail} />
        </div>
      )}

      {open &&
        (matches.compatible.length === 0 ? (
          <p className="placeholder">{t('matching.noUnits')}</p>
        ) : (
          <ol className="matches" aria-label={t('matching.compatibleUnits')}>
            {matches.compatible.map((match) => (
              <PostingMatchCard key={match.posting.id} match={match} load={load} brokerVerified={matches.brokerVerified} />
            ))}
          </ol>
        ))}

      {open && matches.notCompatible.length > 0 && (
        <details className="matches__excluded">
          <summary>{t('matching.notCompatible', { count: matches.notCompatible.length })}</summary>
          <ul>
            {matches.notCompatible.map((match) => (
              <li key={match.posting.id}>
                <Link to={`/availability/${match.posting.id}`}>
                  {unitName(match.posting, t)}
                </Link>{' '}
                <span className="muted">{match.posting.carrierName}</span>
                {' — '}
                {exclusionSentences(match, load, match.posting, t, language).join(' ')}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

function PostingMatchCard({
  match,
  load,
  brokerVerified,
}: {
  match: PostingMatch
  load: Load
  brokerVerified: boolean
}) {
  const { t, i18n } = useTranslation()
  const { posting } = match
  const language = i18n.language

  return (
    <li className="match">
      <p className="match__head">
        <Link to={`/availability/${posting.id}`}>
          {unitName(posting, t)}
        </Link>
        <span className="muted"> · {posting.carrierName}</span>
      </p>
      <p>
        {[
          unitEquipment(posting, t),
          t(`availability.loadTypes.${posting.loadType}`),
          `${posting.city}, ${posting.state}`,
          availableDays(posting, t, language),
        ].join(' · ')}
      </p>
      <p className="match__factors">
        {factorPhrases(match.factors, load.rateUsd, brokerVerified, t, language).join(' · ')}
      </p>
      {cautionSentences(match, load, t).map((sentence) => (
        <p key={sentence} className="alert alert--warning match__caution">
          {sentence}
        </p>
      ))}
    </li>
  )
}
